
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  LogOut,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Sun,
  Moon,
  Globe,
  Eye,
  X,
} from 'lucide-react';
import { UserRole } from '../types.ts';
import { NAV_GROUPS, type NavItem } from '../src/lib/navigation';
import { useAuth } from '../src/contexts/AuthContext';
import { useConfig, getResolvedThemeColors } from '../src/contexts/ConfigContext';
import { useTheme } from '../src/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../src/i18n';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ROUTES, ROUTE_PERMISSIONS, resolveRouteKeyFromPathname } from '../src/routes';
import { isNavDestinationActive, isNavParentActive as isNavParentDestinationActive } from '../src/lib/navActive';
import NotificationBell from './NotificationBell';
import { HEADER_MENU_PANEL, HEADER_MENU_ITEM_PY } from '../src/ui/headerMenu';
import { dataService } from '../services/dataService';
import { useDataRefresh } from '../src/hooks/useDataRefresh';
import { useSSEConnectionStatus } from '../src/hooks/useSSEConnectionStatus';
import { PREVIEWABLE_ROLES } from '../src/lib/viewAsRole';
import { canAccessRoute, isPureClient } from '../src/lib/accessMatrix';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface LayoutProps {
  children: React.ReactNode;
}


const LANGUAGE_NAMES: Record<SupportedLanguage, string> = { es: 'Español', en: 'English' };

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isViewAsMenuOpen, setIsViewAsMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const viewAsMenuRef = useRef<HTMLDivElement>(null);

  const {
    user,
    logout,
    hasRole,
    canUseViewAs,
    viewAsRole,
    isViewingAs,
    setViewAsRole,
    clearViewAsRole,
  } = useAuth();
  const { config } = useConfig();
  const { theme, toggleTheme } = useTheme();
  const resolved = getResolvedThemeColors(config, theme);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Live badge for the "Mailbox" item (15/08, exact template: components/NotificationBell.tsx).
  // Sums the 3 mailboxes (operations/collections/accounting) -- the backend already returns 0
  // for ones this user cannot see or that are not configured (GET /api/mailbox/unread-count).
  const canSeeBuzon = hasRole(ROUTE_PERMISSIONS.mailbox as UserRole[]);
  const [mailboxUnreadCount, setMailboxUnreadCount] = useState(0);
  const sseConnected = useSSEConnectionStatus();

  const refreshMailboxUnreadCount = useCallback(() => {
    if (!canSeeBuzon) return;
    dataService.getMailboxUnreadCount()
      .then((counts) => setMailboxUnreadCount((counts.operations || 0) + (counts.collections || 0) + (counts.accounting || 0)))
      .catch(() => {});
  }, [canSeeBuzon]);

  useEffect(() => {
    refreshMailboxUnreadCount();
  }, [refreshMailboxUnreadCount]);

  // A new IMAP email processed by the operations/collections pollers fires
  // notifyClients(..., { entidad: 'mailbox' }) -- see runOperatorInvoicePoll/runUpsTrackingPoll/
  // runRecaudoEmailPoll in server.ts.
  useDataRefresh(['mailbox'], () => refreshMailboxUnreadCount());

  // Close the profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
      if (viewAsMenuRef.current && !viewAsMenuRef.current.contains(event.target as Node)) {
        setIsViewAsMenuOpen(false);
      }
    };
    if (isUserMenuOpen || isLangMenuOpen || isViewAsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isLangMenuOpen, isViewAsMenuOpen]);

  const navGroups = NAV_GROUPS;

  const visibleNavGroups = navGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => hasRole(item.roles as UserRole[])) }))
    .filter((group) => group.items.length > 0);

  const subItemsFor = (item: NavItem) =>
    (item.subItems || []).filter((sub) => !sub.roles || hasRole(sub.roles as UserRole[]));

  const isSubItemActive = (subId: string) =>
    isNavDestinationActive(subId, location.pathname, location.search);

  const isParentActive = (item: NavItem) =>
    isNavParentDestinationActive(item, location.pathname, location.search);

  useEffect(() => {
    visibleNavGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (subItemsFor(item).length > 0 && isParentActive(item)) {
          setExpandedParents((prev) => ({ ...prev, [item.id]: true }));
        }
      });
    });
  }, [location.pathname, location.search]);

  const handleNavClick = (routePath: string) => {
    navigate(routePath);
    setIsMobileMenuOpen(false);
  };

  const handleSelectViewAs = (role: UserRole | null) => {
    if (role) setViewAsRole(role);
    else clearViewAsRole();
    setIsViewAsMenuOpen(false);

    const currentKey = resolveRouteKeyFromPathname(location.pathname);
    const nextRoles = role ? [role] : (user?.roles ?? []);
    if (currentKey && currentKey !== 'home' && !canAccessRoute(nextRoles, currentKey)) {
      if (role && isPureClient([role])) {
        navigate(ROUTES.clientPortal);
      } else {
        navigate(ROUTES.home);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.home, { replace: true });
  };

  // Item icon + unread badge / SSE connection light when it is "Mailbox" -- same visual
  // style as components/NotificationBell.tsx (red badge top-right, status dot bottom-right),
  // only rescaled to fit the sidebar's 18px icon instead of the bell's 18px button. Badge
  // is omitted when the count is 0 (same criteria as notifications).
  const renderNavIcon = (item: NavItem, isActive: boolean) => {
    const Icon = item.icon;
    const isBuzon = item.id === ROUTES.mailbox;
    return (
      <span className="relative inline-flex shrink-0">
        <Icon size={18} className="shrink-0" style={{ color: isActive ? '#FFFFFF' : undefined }} />
        {isBuzon && mailboxUnreadCount > 0 && (
          <span
            data-testid="mailbox-nav-badge"
            data-tooltip={t('nav.mailboxUnread', { count: mailboxUnreadCount })}
            className="absolute -top-1.5 -right-2 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full bg-rose-600 text-white text-[8px] font-black leading-none"
          >
            {mailboxUnreadCount > 99 ? '99+' : mailboxUnreadCount}
          </span>
        )}
        {isBuzon && (
          <span
            data-testid="mailbox-sse-indicator"
            data-tooltip={sseConnected ? undefined : t('notifications.connectionLost')}
            className={cn(
              'absolute -bottom-1 -right-1 w-2 h-2 rounded-full border border-white dark:border-slate-950',
              sseConnected ? 'bg-emerald-500' : 'bg-slate-400 dark:bg-slate-500'
            )}
          />
        )}
      </span>
    );
  };

  const renderNavButton = (item: NavItem, collapsed: boolean) => {
    const Icon = item.icon;
    const isActive = isParentActive(item);
    const isExpanded = expandedParents[item.id] ?? isActive;
    const visibleSubs = subItemsFor(item);
    const hasSubs = visibleSubs.length > 0;

    if (collapsed) {
      return (
        <div
          key={item.id}
          className="relative group"
          onMouseEnter={() => setHoveredItemId(item.id)}
          onMouseLeave={() => setHoveredItemId(null)}
        >
          <button
            data-tour-id={item.id}
            // With the menu collapsed the button is icon-only: without an accessible
            // name, a screen reader announces "button" eighteen times in a row.
            // The visual tooltip beside it does not help.
            aria-label={t(item.labelKey)}
            aria-current={isActive && !hasSubs ? 'page' : undefined}
            onClick={() => {
              if (hasSubs) {
                handleNavClick(visibleSubs[0].id);
              } else {
                handleNavClick(item.id);
              }
            }}
            className={cn(
              'relative flex items-center justify-center h-11 w-full rounded-xl transition-all duration-300',
              isActive
                ? 'text-white shadow-lg shadow-black/20'
                : item.highlight
                  ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
            )}
            style={{
              backgroundColor: isActive ? (resolved.primaryColor || '#003B71') : undefined
            }}
          >
            {renderNavIcon(item, isActive)}
          </button>

          {!hasSubs && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-slate-800 text-white text-[10px] font-bold rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[210] shadow-xl">
              {t(item.labelKey)}
              {/* With the menu collapsed there is no room for a badge: the label is added
                  to the tooltip, the only thing visible on hover. */}
              {item.comingSoon && <span className="ml-1.5 text-amber-300">· {t('comingSoon.badge')}</span>}
            </div>
          )}

          {hasSubs && hoveredItemId === item.id && (
            <div
              className="absolute left-full top-0 ml-3 w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-2 z-[220] animate-fadeIn"
              onMouseEnter={() => setHoveredItemId(item.id)}
              onMouseLeave={() => setHoveredItemId(null)}
            >
              <div className="label-field px-3 py-2 border-b border-slate-100 dark:border-slate-800 mb-1 flex items-center gap-2">
                <Icon size={14} style={{ color: resolved.primaryColor || '#003B71' }} />
                <span>{t(item.labelKey)}</span>
              </div>
              <div className="space-y-0.5 max-h-72 overflow-y-auto custom-scrollbar">
                {visibleSubs.map((sub) => {
                  const SubIcon = sub.icon || Icon;
                  const subActive = isSubItemActive(sub.id);
                  return (
                    <React.Fragment key={sub.id}>
                    {sub.groupLabelKey && (
                      <p className="label-eyebrow px-3 pb-1 pt-2 text-ink-muted first:pt-0">
                        {t(sub.groupLabelKey)}
                      </p>
                    )}
                    <button
                      data-tour-id={sub.id}
                      data-tooltip={sub.descriptionKey ? t(sub.descriptionKey) : undefined}
                      aria-current={subActive ? 'page' : undefined}
                      aria-description={sub.descriptionKey ? t(sub.descriptionKey) : undefined}
                      onClick={() => {
                        handleNavClick(sub.id);
                        setHoveredItemId(null);
                      }}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold text-left transition-all',
                        subActive
                          ? 'text-white shadow-md'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      )}
                      style={{
                        backgroundColor: subActive ? (resolved.primaryColor || '#003B71') : undefined
                      }}
                    >
                      <SubIcon size={14} className="shrink-0" style={{ color: subActive ? '#FFFFFF' : undefined }} />
                      <span className="truncate">{t(sub.labelKey)}</span>
                    </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={item.id} className="space-y-1">
        <button
          data-tour-id={item.id}
          // Full business name, for anyone who needs the definition rather than just
          // the short label. Also the accessible name: a screen reader announces
          // the full title, not the menu truncation.
          data-tooltip={t(item.labelKey)}
          aria-label={t(item.labelKey)}
          // Without this, the current section was only distinguished by background
          // color: a screen reader had no way to know where the user is.
          aria-current={isActive && !hasSubs ? 'page' : undefined}
          aria-expanded={hasSubs ? isExpanded : undefined}
          onClick={() => {
            if (hasSubs) {
              setExpandedParents((prev) => ({ ...prev, [item.id]: !isExpanded }));
            } else {
              handleNavClick(item.id);
            }
          }}
          className={cn(
            'relative flex items-center justify-between w-full rounded-xl transition-all duration-300 text-left px-3.5 py-2.5',
            isActive
              ? 'text-white shadow-lg shadow-black/20 font-bold'
              : item.highlight
                ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
          )}
          style={{
            backgroundColor: isActive ? (resolved.primaryColor || '#003B71') : undefined
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {renderNavIcon(item, isActive)}
            {/* The menu used to clip labels at the default width: "QUOTER …",
                "OPERATIO…", "AUDIT CENTER AND …" (UX_AUDIT H-05). Two Air Freight
                entries that start the same and get clipped are indistinguishable:
                the map stops helping recognition and forces memorization.

                Fixed on both causes at once, without widening the sidebar:

                1. Sentence case instead of small caps with wide tracking. Small
                   caps widen the text 15-20%; in sentence case these same labels fit.
                2. Short name for the menu (`nav.short.*`). The full business name
                   is not lost: it stays in `title`, and it is what heads the page
                   on entry. The menu needs a label, not a definition.

                `line-clamp-2` remains a safety net for a longer language or future
                label: wraps to two lines instead of clipping. */}
            <span
              className={cn(
                'text-[0.8125rem] font-medium leading-tight line-clamp-2',
                isActive && 'font-semibold',
                item.comingSoon && !isActive && 'opacity-70'
              )}
            >
              {t(`nav.short.${item.labelKey.split('.').pop()}`, { defaultValue: t(item.labelKey) })}
            </span>
          </div>
          {/* Module ready but disabled: the label lives on the item itself so it's understood
              before entering, not after. */}
          {item.comingSoon && (
            <span className="shrink-0 ml-2 px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 text-[8px] font-black uppercase tracking-wider">
              {t('comingSoon.badge')}
            </span>
          )}
          {hasSubs && (
            <ChevronDown
              size={14}
              className={cn('shrink-0 transition-transform duration-200 text-slate-400', isExpanded && 'rotate-180')}
            />
          )}
        </button>

        {hasSubs && (
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden pl-3 pr-1 space-y-0.5 border-l-2 border-slate-200 dark:border-slate-800 ml-4 my-1"
              >
                {visibleSubs.map((sub) => {
                  const SubIcon = sub.icon || Icon;
                  const subActive = isSubItemActive(sub.id);
                  return (
                    <React.Fragment key={sub.id}>
                    {sub.groupLabelKey && (
                      <p className="label-eyebrow px-3 pb-1 pt-2.5 opacity-70 first:pt-0.5">
                        {t(sub.groupLabelKey)}
                      </p>
                    )}
                    <button
                      data-tour-id={sub.id}
                      data-tooltip={sub.descriptionKey ? t(sub.descriptionKey) : undefined}
                      aria-description={sub.descriptionKey ? t(sub.descriptionKey) : undefined}
                      // The current page is usually a sub-item (e.g. Operations → History),
                      // not the parent entry: without this, the active view was not
                      // announced at any menu level.
                      aria-current={subActive ? 'page' : undefined}
                      onClick={() => handleNavClick(sub.id)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-left transition-all duration-200',
                        subActive
                          ? 'font-bold border-l-2'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/60'
                      )}
                      style={{
                        backgroundColor: subActive
                          ? (resolved.sidebarTheme === 'brand'
                              ? 'rgba(255, 255, 255, 0.2)'
                              : theme === 'dark' || resolved.sidebarTheme === 'dark'
                                ? 'rgba(59, 130, 246, 0.18)'
                                : `${resolved.primaryColor || '#003B71'}18`)
                          : undefined,
                        color: subActive
                          ? (resolved.sidebarTheme === 'brand'
                              ? '#FFFFFF'
                              : theme === 'dark' || resolved.sidebarTheme === 'dark'
                                ? '#93C5FD'
                                : (resolved.primaryColor || '#003B71'))
                          : undefined,
                        borderColor: subActive
                          ? (resolved.sidebarTheme === 'brand'
                              ? '#FFFFFF'
                              : theme === 'dark' || resolved.sidebarTheme === 'dark'
                                ? '#60A5FA'
                                : (resolved.primaryColor || '#003B71'))
                          : undefined
                      }}
                    >
                      <SubIcon
                        size={14}
                        className="shrink-0"
                        style={{
                          color: subActive
                            ? (resolved.sidebarTheme === 'brand'
                                ? '#FFFFFF'
                                : theme === 'dark' || resolved.sidebarTheme === 'dark'
                                  ? '#93C5FD'
                                  : (resolved.primaryColor || '#003B71'))
                            : undefined
                        }}
                      />
                      <span className="truncate">{t(sub.labelKey)}</span>
                    </button>
                    </React.Fragment>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  const sidebarContent = (collapsed: boolean) => (
    <div className="flex flex-col h-full relative">
      {/* Floating collapse/expand button on the sidebar edge (Desktop) */}
      <button
        onClick={() => setIsCollapsed((v) => !v)}
        // The `|| 'Expand menu'` that used to be here never fired: when a key is missing,
        // i18next returns the key (truthy), not an empty string -- so the tooltip literally
        // showed "sidebar.expand" to the user. Caught by an e2e test on 11/08; the key now
        // exists in es/en and the misleading fallback is gone.
        aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        data-tooltip={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        className="hidden lg:flex items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-brand-600 shadow-md hover:shadow-lg transition-all absolute top-4 -right-3.5 z-[110] cursor-pointer"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>

      {/* Header with company logo */}
      <div className={cn('flex items-center h-16 shrink-0 border-b px-4', resolved.sidebarTheme === 'light' ? 'border-slate-200' : 'border-slate-800/60', collapsed ? 'justify-center' : 'justify-start gap-3')}>
        <div
          className={cn('flex items-center cursor-pointer min-w-0', collapsed ? 'justify-center' : 'gap-3 flex-1')}
          onClick={() => {
            if (collapsed) {
              setIsCollapsed(false);
            } else {
              handleNavClick(ROUTES.home);
            }
          }}
          data-tooltip={collapsed ? t('sidebar.expand') || 'Expandir' : config.companyName}
        >
          <div className="w-9 h-9 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden shrink-0 shadow-sm transition-transform hover:scale-105 border border-slate-200/50 dark:border-slate-700/50">
            <img src={config.logoUrl} alt={config.appName} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
          </div>
          {!collapsed && (
            <span
              className={cn(
                'font-black text-sm tracking-tight leading-none truncate',
                resolved.sidebarTheme === 'brand'
                  ? 'text-white'
                  : resolved.sidebarTheme === 'light'
                    ? 'text-slate-900'
                    : 'text-slate-100'
              )}
            >
              {config.companyName}
            </span>
          )}
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 space-y-5">
        {visibleNavGroups.map((group) => (
          <div key={group.labelKey} className={collapsed ? 'px-2' : 'px-3'}>
            {!collapsed && (
              // 9px with `tracking-[0.2em]` in light gray was unreadable even with good
              // eyesight. Small caps DO make sense here -- once per group, as an
              // accent -- but at the system's size and contrast.
              //
              // Color comes from attenuated `currentColor`, not an ink token: the
              // sidebar has three themes (brand, light, dark) and the container
              // already sets the right text color for each. A fixed token would
              // work in one of the three and vanish in the other two.
              <p className="mb-1.5 px-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] opacity-60">
                {t(group.labelKey)}
              </p>
            )}
            <div className="space-y-1">
              {group.items.map((item) => renderNavButton(item, collapsed))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-canvas">
      {/* Desktop Sidebar */}
      <aside
        data-app-sidebar
        className={cn(
          'hidden lg:flex flex-col fixed top-0 left-0 h-screen z-[100] transition-all duration-300 border-r border-slate-200 dark:border-slate-800',
          resolved.sidebarTheme === 'brand'
            ? 'text-white'
            : resolved.sidebarTheme === 'light'
              ? 'bg-white text-slate-900 border-slate-200'
              : 'bg-slate-900 text-white border-slate-800',
          isCollapsed ? 'w-[76px]' : 'w-[264px]'
        )}
        style={{
          backgroundColor: resolved.sidebarTheme === 'brand' ? (resolved.primaryColor || '#003B71') : (resolved.sidebarTheme === 'light' ? '#FFFFFF' : '#0F172A')
        }}
      >
        {sidebarContent(isCollapsed)}
      </aside>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 z-[190] bg-black/50 backdrop-blur-sm"
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
              data-app-sidebar
              className={cn(
                'lg:hidden fixed top-0 left-0 h-screen w-[280px] z-[200] border-r border-slate-200 dark:border-slate-800',
                resolved.sidebarTheme === 'brand'
                  ? 'text-white'
                  : resolved.sidebarTheme === 'light'
                    ? 'bg-white text-slate-900 border-slate-200'
                    : 'bg-slate-900 text-white border-slate-800'
              )}
              style={{
                backgroundColor: resolved.sidebarTheme === 'brand' ? (resolved.primaryColor || '#003B71') : (resolved.sidebarTheme === 'light' ? '#FFFFFF' : '#0F172A')
              }}
            >
              {sidebarContent(false)}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Content column */}
      <div className={cn('flex-1 flex flex-col min-w-0 transition-all duration-300', isCollapsed ? 'lg:ml-[76px]' : 'lg:ml-[264px]')}>
        {/* Top bar */}
        <header className="bg-white dark:bg-slate-900 sticky top-0 z-[90] border-b border-slate-200 dark:border-slate-800 shadow-sm">
          <div className="w-full px-3 sm:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors shrink-0"
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label={t('sidebar.openMenu')}
              >
                <Menu size={22} />
              </button>
            </div>
            <span className="lg:hidden min-w-0 font-bold text-sm text-brand-600 truncate">{config.companyName}</span>
            <div className="flex-1" />

            {/* Top header controls -- compact below sm so the user menu never leaves a 360-390 px screen (T-20) */}
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              {canUseViewAs && (
                <div className="relative shrink-0" ref={viewAsMenuRef}>
                  <button
                    onClick={() => setIsViewAsMenuOpen(!isViewAsMenuOpen)}
                    aria-haspopup="menu"
                    aria-expanded={isViewAsMenuOpen}
                    aria-label={t('sidebar.viewAs')}
                    data-tooltip={t('sidebar.viewAs')}
                    className={cn(
                      'flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-xl transition-all border text-xs font-bold cursor-pointer',
                      isViewingAs
                        ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800'
                        : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
                    )}
                  >
                    <Eye size={14} className={isViewingAs ? 'text-amber-600' : 'text-brand-600'} />
                    <span className="hidden sm:inline">{t('sidebar.viewAs')}</span>
                    <ChevronDown size={12} className={cn('hidden sm:block text-slate-400 transition-transform', isViewAsMenuOpen ? 'rotate-180' : '')} />
                  </button>

                  {isViewAsMenuOpen && (
                    <div className={cn(HEADER_MENU_PANEL, 'sm:w-56')}>
                      <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 mb-1">
                        <p className="label-field">{t('sidebar.viewAsTitle')}</p>
                      </div>
                      {isViewingAs && (
                        <button
                          onClick={() => handleSelectViewAs(null)}
                          className={cn('w-full flex items-center gap-2 px-3 text-xs font-bold text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors', HEADER_MENU_ITEM_PY)}
                        >
                          <X size={14} />
                          {t('sidebar.viewAsExit')}
                        </button>
                      )}
                      {PREVIEWABLE_ROLES.map((role) => {
                        const selected = viewAsRole === role;
                        return (
                          <button
                            key={role}
                            onClick={() => handleSelectViewAs(role)}
                            className={cn(
                              'w-full flex items-center justify-between px-3 text-xs font-bold transition-colors', HEADER_MENU_ITEM_PY,
                              selected
                                ? 'bg-brand-600 text-white'
                                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                            )}
                          >
                            <span>{t(`roles.labels.${role}`)}</span>
                            {selected && <span className="text-[10px] uppercase opacity-80">{t('sidebar.viewAsActive')}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Dark/Light mode toggle */}
              <button
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}
                data-tooltip={theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}
                className="p-2 sm:p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer border border-slate-200 dark:border-slate-800"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* In-app notification center */}
              <NotificationBell />

              {/* Custom language selector */}
              <div className="relative shrink-0" ref={langMenuRef}>
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  aria-haspopup="menu"
                  aria-expanded={isLangMenuOpen}
                  aria-label={t('sidebar.language')}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl transition-all border border-slate-200 dark:border-slate-700 text-xs font-black uppercase cursor-pointer"
                >
                  <Globe size={14} className="text-brand-600" />
                  <span>{(i18n.resolvedLanguage || i18n.language).toUpperCase()}</span>
                  <ChevronDown size={12} className={cn('hidden sm:block text-slate-400 transition-transform', isLangMenuOpen ? 'rotate-180' : '')} />
                </button>

                {isLangMenuOpen && (
                  <div className={cn(HEADER_MENU_PANEL, 'sm:w-44')}>
                    <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 mb-1">
                      <p className="label-field">{t('sidebar.language')}</p>
                    </div>
                    {SUPPORTED_LANGUAGES.map((lng) => {
                      const isSelected = (i18n.resolvedLanguage || i18n.language) === lng;
                      return (
                        <button
                          key={lng}
                          onClick={() => {
                            i18n.changeLanguage(lng);
                            setIsLangMenuOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center justify-between px-3 text-xs font-bold transition-colors', HEADER_MENU_ITEM_PY,
                            isSelected
                              ? 'bg-brand-600 text-white'
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                          )}
                        >
                          <span>{LANGUAGE_NAMES[lng]}</span>
                          <span className={cn('text-[10px] font-black uppercase px-1.5 py-0.5 rounded', isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500')}>
                            {lng}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* User menu with click-outside */}
              <div className="relative shrink-0" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  aria-label={t('sidebar.account')}
                  className="flex items-center gap-2 p-1 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl transition-all border border-slate-200 dark:border-slate-700 group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-xl bg-white dark:bg-slate-900 shadow-sm flex items-center justify-center text-brand-600 font-bold border border-slate-100 dark:border-slate-700 text-xs">
                    {(user?.name ?? user?.nombre ?? '?').charAt(0)}
                  </div>
                  <div className="hidden lg:block text-left max-w-[120px]">
                    <p className="text-[10px] font-bold text-slate-900 dark:text-slate-100 leading-none truncate">{user?.name ?? user?.nombre}</p>
                    <p className="label-field font-medium mt-1 truncate">
                      {isViewingAs && viewAsRole
                        ? t(`roles.labels.${viewAsRole}`)
                        : user?.roles[0]}
                    </p>
                  </div>
                  <ChevronDown size={12} className={cn('hidden sm:block ml-1 text-slate-400 transition-transform', isUserMenuOpen ? 'rotate-180' : '')} />
                </button>

                {isUserMenuOpen && (
                  <div className={cn(HEADER_MENU_PANEL, 'sm:w-52')}>
                    <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 mb-1">
                      <p className="label-field">{t('sidebar.account')}</p>
                      <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate mt-0.5">{user?.name ?? user?.nombre}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate(ROUTES.profile);
                        setIsUserMenuOpen(false);
                      }}
                      className={cn('w-full flex items-center gap-3 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors', HEADER_MENU_ITEM_PY, 'sm:py-2.5')}
                    >
                      <UserIcon size={16} /> {t('sidebar.profile')}
                    </button>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleLogout();
                      }}
                      className={cn('w-full flex items-center gap-3 px-4 text-xs font-bold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors', HEADER_MENU_ITEM_PY, 'sm:py-2.5')}
                    >
                      <LogOut size={16} /> {t('sidebar.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {isViewingAs && viewAsRole && (
          <div
            role="status"
            className="sticky top-16 z-[85] flex items-center justify-center gap-3 px-4 py-2 text-xs font-bold border-b bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950/50 dark:text-amber-100 dark:border-amber-900"
          >
            <Eye size={14} className="shrink-0" aria-hidden="true" />
            <span>{t('sidebar.viewAsBanner', { role: t(`roles.labels.${viewAsRole}`) })}</span>
            <button
              type="button"
              onClick={() => handleSelectViewAs(null)}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-300 dark:border-amber-700 px-2 py-1 hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors"
            >
              <X size={12} aria-hidden="true" />
              {t('sidebar.viewAsExit')}
            </button>
          </div>
        )}

        {/* Main content.
            `max-w-7xl` (1280px) was an invisible cap that undid work done
            downstream: FIFTEEN page files declare `max-w-[1600px]` for their wide
            tables, and this container clipped them to 1280px with no visible
            reason. In Operations that helped cause the table to overflow
            horizontally at 1440px screen width (UX_AUDIT H-01): it asked for
            ~1500px and only got 1280 minus padding.

            Content width is decided HERE, in one place. Page-level
            `max-w-[1600px]` declarations remain harmless redundancy (same value). */}
        <main className="mx-auto w-full min-w-0 max-w-[1600px] grow px-4 py-6">
          <div className="animate-fadeIn">
            {children}
          </div>
        </main>

        {/* Footer.
            Previously took ~110px on EVERY screen to say nothing: a 10px line with
            `tracking-[0.3em]` in below-AA gray, and three colored dots that were
            neither navigation, status, nor pagination (UX_AUDIT H-20).

            Now takes a third of that and carries the one signal the user always
            needs in view: whether the real-time channel is alive. The whole app
            updates itself via SSE; when that channel drops, what is on screen
            stays valid but stops refreshing, and until now that was only knowable
            by opening the notification bell. */}
        <footer className="mt-auto border-t border-hairline bg-surface px-4 py-3">
          <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-2">
            <p className="text-[0.75rem] text-ink-muted">
              &copy; {new Date().getFullYear()} {config.companyName}
            </p>
            <p
              className="flex items-center gap-1.5 text-[0.75rem] text-ink-muted"
              // `role="status"` not `alert`: background information; must not interrupt
              // what the user is reading when it changes.
              role="status"
            >
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: sseConnected ? 'var(--color-positive)' : 'var(--color-caution)' }}
              />
              {sseConnected ? t('sidebar.liveConnected') : t('sidebar.liveDisconnected')}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
